import { useTranslation } from "next-i18next";
import { NextSeo } from "next-seo";
import { useRouter } from "next/router";
import { GetStaticProps } from "next";
import {
  BriefingView,
  INVASION_STATIC_GAME_DATA_QUERY_KEY,
  StaticGameData,
  multicallStaticGameData,
} from "@looksrare/invasion";
import { createPublicClient, http } from "viem";
import { QueryClient, dehydrate } from "@tanstack/react-query";
import { IS_TESTNET } from "@looksrare/config";
import { useCurrentInvasionChainInfo } from "@looksrare/utils";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { blastSepolia, blast } from "viem/chains";
import { getCanonicalAndLanguageAlternates } from "config/seo";
import ErrorBoundary from "components/ErrorBoundary";

interface PageProps {
  staticGameData: StaticGameData;
}

const InvasionBriefingPage: React.FC<PageProps> = () => {
  const { t } = useTranslation();
  const router = useRouter();
  const currentChainInfo = useCurrentInvasionChainInfo();
  const title = "INVASION";
  const description =
    "Say hello to 50,000 of the galaxy's finest soldiers, mercenaries and black-market contractors. You'll put together a crack team of these alien reprobates for a lucrative heist in LooksRare: INVASION";
  const { canonical, languageAlternates } = getCanonicalAndLanguageAlternates(
    "/invasion",
    router,
    currentChainInfo.appUrl
  );

  return (
    <ErrorBoundary>
      <NextSeo
        title={title}
        description={description}
        openGraph={{
          url: canonical,
          title: t("{{pageTitle}} | LooksRare", { pageTitle: title }),
          description: description,
          images: [
            {
              url: `${currentChainInfo.appUrl}/images/invasion/meta.png`,
              alt: title,
              width: 1600,
              height: 900,
              type: "image/png",
            },
          ],
        }}
        canonical={canonical}
        languageAlternates={languageAlternates}
      />
      <BriefingView />
    </ErrorBoundary>
  );
};

export default InvasionBriefingPage;

// @note - we have no mechanism for multi-chain support
export const getStaticProps: GetStaticProps = async ({ locale = "en" }) => {
  try {
    const queryClient = new QueryClient();
    const chain = IS_TESTNET ? blastSepolia : blast;
    const client = createPublicClient({
      chain,
      transport: http(chain.rpcUrls.default.http[0]),
    });

    const staticGameData = await multicallStaticGameData(chain.id, client);
    queryClient.setQueryData(INVASION_STATIC_GAME_DATA_QUERY_KEY, staticGameData);

    return {
      props: {
        ...(await serverSideTranslations(locale)),
        locale,
        staticGameData,
        dehydratedState: dehydrate(queryClient),
      },
      /**
       * @note - The page's cache times are managed via `CDN-` & `Vercel-CDN-` cache-control headers in next.config.js
       */
      revalidate: 86400, // 24hr
    };
  } catch (e) {
    // @todo-invasion page fetching - reinstate throwing on catch
    // throw new PageDataFetchError("/invasion/mission", e);
    return {
      props: {
        ...(await serverSideTranslations(locale)),
        locale,
      },
    };
  }
};
